import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import {
  equipmentDocumentsByEquipmentID,
  equipmentTypeDocumentsByEquipmentTypeID,
  getEquipment,
  getEquipmentType,
} from "graphql/queries";
import { deleteEquipmentDocument } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import EquipmentParamatersTable from "components/equipments/documents/EquipmentDocumentsTable";
import EquipmentDocumentCreateForm from "./EquipmentDocumentCreateForm";
import EquipmentDocumentUpdateForm from "./EquipmentDocumentUpdateForm";

const EquipmentDocuments = ({
  equipmentID,
  equipmentName,
  visible,
  onClose,
}) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "equipment:create-doc",
    "equipment:update-doc",
    "equipment:delete-doc",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [equipmentDocuments, setEquipmentDocuments] = useState([]);
  const [equipmentDocumentsLoading, setEquipmentDocumentsLoading] =
    useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentDocumentId: null,
    equipmentDocumentName: null,
  });
  const [modalEquipmentDocument, setModalEquipmentDocument] = useState({
    visible: false,
    equipmentDocumentId: null,
  });

  const columnsMapped = useMemo(
    () =>
      equipmentDocuments.map((item) => {
        return {
          ...item,
          actions: item.byType ? (
            <span className="text-muted">
              {strings.formatString(strings.documentReturned, {
                name: item.byType,
              })}
            </span>
          ) : (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              <Button
                className="px-1"
                children={strings.actions.download}
                variation="link"
                size="small"
                onClick={() => downloadDoc(item.fileSrc)}
              />
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      equipmentDocumentId: item.id,
                      equipmentDocumentName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipmentDocuments]
  );

  const onNext = () => {
    fetchEquipmentDocuments(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchEquipmentDocuments(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchEquipmentDocuments(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalEquipmentDocument({
      visible: true,
      action,
      equipmentDocumentId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalEquipmentDocument({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchEquipmentDocuments();
  };

  const downloadDoc = async (src) => {
    try {
      const res = await Storage.get(`equipments/${src}`, {
        level: "public",
      });
      const link = document.createElement("a");
      link.href = res;
      link.setAttribute("download", "");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.log("Error Download Document", err);
      toast.error(strings.responses.errorDownload);
    }
  };

  async function fetchEquipmentDocuments(token, isNext = true, keywords) {
    try {
      setEquipmentDocumentsLoading(true);
      const limitMapped = 10;
      const equipmentData = await API.graphql(
        graphqlOperation(getEquipment, { id: equipmentID })
      );
      const equipmentItem = equipmentData.data.getEquipment;

      const equipmentDocumentsData = await API.graphql(
        graphqlOperation(equipmentDocumentsByEquipmentID, {
          equipmentID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      fileName: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      let equipmentDocumentsItems =
        equipmentDocumentsData.data.equipmentDocumentsByEquipmentID.items;

      if (equipmentItem.equipmentTypeID) {
        const equipmentTypeData = await API.graphql(
          graphqlOperation(getEquipmentType, {
            id: equipmentItem.equipmentTypeID,
          })
        );
        const equipmentTypeItem = equipmentTypeData.data.getEquipmentType;
        const documentsRelTypeData = await API.graphql(
          graphqlOperation(equipmentTypeDocumentsByEquipmentTypeID, {
            equipmentTypeID: equipmentItem.equipmentTypeID,
          })
        );
        const documentsRelTypeItems =
          documentsRelTypeData.data.equipmentTypeDocumentsByEquipmentTypeID
            .items || [];
        equipmentDocumentsItems = [
          ...equipmentDocumentsItems,
          ...documentsRelTypeItems.map((p) => ({
            ...p,
            byType: equipmentTypeItem.name,
          })),
        ];
      }

      setEquipmentDocuments(equipmentDocumentsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            equipmentDocumentsData.data.equipmentDocumentsByEquipmentID
              .nextToken,
        };
      });
      setEquipmentDocumentsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentDocumentsLoading(false);
    } finally {
      setEquipmentDocumentsLoading(false);
    }
  }

  async function equipmentDocumentDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteEquipmentDocument, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const { id: deletedId, fileSrc: deletedSrc } =
        result.data.deleteEquipmentDocument;
      setEquipmentDocuments(
        equipmentDocuments.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        equipmentDocumentId: null,
      });

      if (deletedSrc)
        await Storage.remove(`equipments/${deletedSrc}`, {
          level: "public",
        });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipmentDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalEquipmentDocument.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {equipmentName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addEquipmentDocument}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {equipmentDocumentsLoading ? (
                  <Loader />
                ) : (
                  <EquipmentParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.equipmentDocumentName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    equipmentDocumentId: null,
                  })
                }
                onConfirm={() =>
                  equipmentDocumentDelete(modalDetails.equipmentDocumentId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalEquipmentDocument.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipmentDocument.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipmentDocument.action === "create" ? (
                <>
                  {strings.modalEquipmentDocument.addTitle} - {equipmentName}
                </>
              ) : (
                <>
                  {strings.modalEquipmentDocument.updateTitle} - {equipmentName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipmentDocument.action === "create" ? (
                <EquipmentDocumentCreateForm
                  equipmentID={equipmentID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <EquipmentDocumentUpdateForm
                  equipmentDocumentId={
                    modalEquipmentDocument.equipmentDocumentId
                  }
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Documentos de Equipo",
    successDeleted: "Documento de Equipo eliminado con éxito",
    actions: {
      addEquipmentDocument: "Agregar Documento de Equipo",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      download: "Descargar",
    },
    responses: {
      errorFetch: "Error al consultar documento de equipos",
      errorDownload: "Error en la descarga",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el documento de equipo {name}?",
    },
    modalEquipmentDocument: {
      addTitle: "Agregar Documento de Equipo",
      updateTitle: "Actualizar Documento de Equipo",
    },
    documentReturned: "Heredado del Tipo de Equipo {name}",
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default EquipmentDocuments;
